import { Component } from '@theme/component';

/**
 * Reveals its contents once they scroll into view.
 *
 * Wraps every DRAPP section, so the whole store inherits one consistent
 * entrance rhythm instead of each block hand-rolling its own.
 *
 * Two deliberate guards:
 * 1. Content already on screen at load is never hidden — hiding it would
 *    cause a visible flash before the observer's first callback runs.
 * 2. `prefers-reduced-motion` short-circuits to the shown state, so the
 *    animation is skipped entirely rather than merely shortened.
 *
 * @extends {Component}
 */
export class DrappReveal extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.dataset.reveal = 'shown';
      return;
    }

    if (this.getBoundingClientRect().top < window.innerHeight * 0.9) {
      this.dataset.reveal = 'shown';
      return;
    }

    this.dataset.reveal = 'hidden';
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.dataset.reveal = 'shown';
          this.#stopObserving();
        }
      },
      { rootMargin: '0px 0px -12% 0px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#stopObserving();
  }

  #stopObserving() {
    this.#observer?.disconnect();
    this.#observer = null;
  }
}

if (!customElements.get('drapp-reveal-component')) {
  customElements.define('drapp-reveal-component', DrappReveal);
}
